/**
 * PDF 페이지 배치 규칙 — 미리보기와 실제 출력이 같은 결과를 내도록 한 곳에 모아 둔다.
 *
 * 예전에는 위쪽(섹션 생성)과 아래쪽(mm 산술)이 서로 다른 기준으로 두 번 나눴고, 그 불일치가
 * 백지 페이지·상단 대공백·행 잘림의 원인이었다(WEB-2264). 미리보기까지 따로 계산하면 같은
 * 사고가 한 번 더 난다. 그래서 "어디에 놓을지"는 이 함수 하나만 안다.
 *
 * 규칙
 *   1. 남은 자리에 들어가면 놓는다
 *   2. 안 들어가면 페이지를 넘기고 통째로 놓는다
 *   3. 한 페이지보다 큰 덩어리만 예외적으로 잘라 이어 붙인다
 *
 * 단위는 mm. 미리보기에서 px 로 쓰려면 mmPerPx() 로 환산한다.
 */

/** A4 세로 (mm) */
export const A4_HEIGHT_MM = 297
export const A4_WIDTH_MM = 210
/** 상·하 여백 */
export const PAGE_MARGIN_MM = 10
/** 페이지 번호가 들어가는 하단 영역 */
export const PAGE_FOOTER_HEIGHT_MM = 10
/** 덩어리 사이 간격 */
export const CHUNK_GAP_MM = 3

/**
 * 상단 요약 묶음(템플릿 이름 · 서머리)과 그 아래 첫 컴포넌트 사이 간격.
 * 요약끼리는 붙여 두되 본문과는 눈에 띄게 떨어뜨린다.
 */
export const HEADER_BOTTOM_GAP_MM = 36

/**
 * 콘텐츠 가로 폭 — 좌우 여백을 상·하단과 같은 10mm 로 두고 남는 폭.
 * daily 는 원래 190mm 였고 trend 만 183.5mm 라 같은 리포트가 페이지마다 다른 크기로 나왔다.
 */
export const CONTENT_WIDTH_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2

/** 콘텐츠가 놓이는 세로 구간 [top, bottom) */
export const CONTENT_TOP_MM = PAGE_MARGIN_MM
export const CONTENT_BOTTOM_MM = A4_HEIGHT_MM - PAGE_MARGIN_MM - PAGE_FOOTER_HEIGHT_MM
/** 한 페이지에 담을 수 있는 최대 콘텐츠 높이 */
export const MAX_CONTENT_HEIGHT_MM = CONTENT_BOTTOM_MM - CONTENT_TOP_MM

/**
 * 미리보기 1px 이 출력물에서 몇 mm 인지.
 * 출력 폭(mm)은 고정이고 미리보기 폭(px)도 PDF_PREVIEW_WIDTH_PX 로 고정이므로 상수가 된다.
 */
export const mmPerPx = (contentWidthMm: number, previewWidthPx: number) =>
  contentWidthMm / previewWidthPx

/**
 * 페이지를 세로로 6등분한 칸(1/6 = 44.5mm).
 * 컴포넌트마다 차지하는 칸 수가 정해져 있고, 칸 경계에 맞춰 놓는다.
 */
export const UNITS_PER_PAGE = 6

/**
 * 컴포넌트 종류. 종류가 다르면 같은 페이지에 두지 않는다 —
 * 단 HEADER(템플릿 이름·상단 요약)만 예외로 어떤 페이지에나 함께 놓인다.
 */
export const CHUNK_TYPE = {
  /** 템플릿 이름 · 상단 요약 — 각 1/6 */
  HEADER: "HEADER",
  /** 차트 한 개 — 1/3. Chart by Player · Session Details · Scatter · 액션맵이 모두 여기 속한다 */
  CHART: "CHART",
  /** Stats 표 */
  STATS: "STATS",
  /** Custom Table */
  CUSTOM: "CUSTOM",
  /** 액션맵 — 코트와 액션 표를 나란히 놓아야 해서 한 페이지를 통째로 쓴다 */
  ACTION_MAP: "ACTION_MAP",
} as const

export type ChunkType = (typeof CHUNK_TYPE)[keyof typeof CHUNK_TYPE]

/**
 * 종류별 칸 수. 0 은 "자기 높이만큼" — 정해진 크기 없이 같은 종류끼리 페이지를 채운다.
 * (표는 조각 높이가 제각각이라 칸을 고정하면 페이지가 크게 빈다)
 */
export const CHUNK_UNITS: Record<ChunkType, number> = {
  [CHUNK_TYPE.HEADER]: 1,
  [CHUNK_TYPE.CHART]: 2,
  [CHUNK_TYPE.STATS]: 0,
  [CHUNK_TYPE.CUSTOM]: 0,
  // 액션맵은 코트(세로로 긴 비율)와 액션 표를 나란히 놓는다. 1/3 페이지에 넣으면 표가
  // 9행에서 잘려 나머지 선수가 조용히 사라진다 — 한 페이지를 통째로 준다.
  [CHUNK_TYPE.ACTION_MAP]: UNITS_PER_PAGE,
}

/** 다른 종류와 같은 페이지에 놓일 수 있는 종류 */
export const MIXABLE_CHUNK_TYPES: ChunkType[] = [CHUNK_TYPE.HEADER]

/** 한 페이지보다 큰 덩어리를 잘라 이어 붙일 때의 조각 */
export type PageSlice = {
  /** 1-based */
  page: number
  /** 페이지 상단 기준 y (mm) */
  y: number
  /** 원본 덩어리 위쪽에서 얼마나 떨어진 지점부터인지 (mm) */
  sourceOffset: number
  height: number
}

export type Placement = {
  /** 입력 배열에서의 위치 */
  index: number
  /** 높이가 0 이하라 배치하지 않은 덩어리는 false */
  placed: boolean
  /** 1-based. placed 가 false 면 0 */
  page: number
  /** 페이지 상단 기준 y (mm). placed 가 false 면 0 */
  y: number
  height: number
  /** 잘려 이어 붙은 경우의 조각들. 비어 있으면 page/y 에 통째로 놓인 것이다. */
  slices: PageSlice[]
  /** 앞 덩어리와 같은 페이지에 이어 놓지 못하고 페이지를 넘겨서 놓은 경우 */
  startedNewPage: boolean
}

export type PaginateResult = {
  placements: Placement[]
  totalPages: number
}

export type PaginateOptions = {
  top?: number
  bottom?: number
  gap?: number
  /**
   * 덩어리별 종류. 넘기면 칸(1/6) 단위 배치로 동작한다 — 종류가 다르면 페이지를 넘기고,
   * 각 덩어리는 자기 칸 수만큼 자리를 잡는다. 넘기지 않으면 종전대로 흘려 담는다.
   */
  types?: ChunkType[]
}

/**
 * 덩어리 높이(mm) 목록을 페이지에 배치한다.
 *
 * 백지 페이지가 구조적으로 생기지 않는다 — 페이지를 넘기는 경로가 "넘긴 직후 반드시 무언가를
 * 놓는" 두 곳뿐이기 때문이다(규칙 2, 규칙 3 의 다음 조각).
 */
export const paginate = (heights: number[], options: PaginateOptions = {}): PaginateResult => {
  const top = options.top ?? CONTENT_TOP_MM
  const bottom = options.bottom ?? CONTENT_BOTTOM_MM
  const gap = options.gap ?? CHUNK_GAP_MM
  const maxContentHeight = bottom - top
  const types = options.types

  if (types) {
    return paginateByUnits(heights, types, top, bottom)
  }

  let page = 1
  let y = top

  const spaceLeft = () => bottom - y

  const placements = heights.map((height, index): Placement => {
    if (height <= 0) {
      return { index, placed: false, page: 0, y: 0, height, slices: [], startedNewPage: false }
    }

    // 규칙 1 — 남은 자리에 들어간다
    if (height <= spaceLeft()) {
      const placed = { index, placed: true, page, y, height, slices: [], startedNewPage: false }
      y += height + gap

      return placed
    }

    // 규칙 2 — 페이지 하나에는 들어가는 덩어리는 잘라 그리지 않고 다음 페이지에 통째로 (WEB-2264)
    if (height <= maxContentHeight) {
      page += 1
      y = top
      const placed = { index, placed: true, page, y, height, slices: [], startedNewPage: true }
      y += height + gap

      return placed
    }

    // 규칙 3 — 한 페이지보다 큰 덩어리만 잘라 이어 붙인다
    const slices: PageSlice[] = []
    let remaining = height
    let sourceOffset = 0

    while (remaining > 0) {
      const sliceHeight = Math.min(remaining, spaceLeft())
      slices.push({ page, y, sourceOffset, height: sliceHeight })

      remaining -= sliceHeight
      sourceOffset += sliceHeight

      if (remaining > 0) {
        page += 1
        y = top
      } else {
        y += sliceHeight + gap
      }
    }

    return {
      index,
      placed: true,
      page: slices[0].page,
      y: slices[0].y,
      height,
      slices,
      startedNewPage: false,
    }
  })

  return { placements, totalPages: page }
}

/**
 * 칸(1/6) 단위 배치.
 *
 * 칸은 "한 페이지에 몇 개까지" 를 정하는 수용량 계산에만 쓰고, 실제 배치는 높이대로 붙여
 * 놓는다 — 칸 경계에 스냅하면 템플릿 이름(18mm)과 서머리(24mm)처럼 작은 것들이 각자 한 칸
 * (44.5mm)씩 차지하면서 사이가 벌어진다.
 *
 * 종류가 다르면 자리가 남아도 페이지를 넘긴다 — 성격이 다른 컴포넌트가 한 장에 섞이지 않게.
 * 실제 높이가 정해진 칸 수보다 크면 실제에 맞춰 칸을 더 쓴다(디자인 의도보다 안전이 우선).
 */
const paginateByUnits = (
  heights: number[],
  types: ChunkType[],
  top: number,
  bottom: number,
): PaginateResult => {
  const unitHeight = (bottom - top) / UNITS_PER_PAGE
  const gap = CHUNK_GAP_MM

  let page = 1
  let y = top
  let unitsUsed = 0
  let pageType: ChunkType | null = null
  let prevWasHeader = false

  const placements = heights.map((height, index): Placement => {
    if (height <= 0) {
      return { index, placed: false, page: 0, y: 0, height, slices: [], startedNewPage: false }
    }

    const type = types[index] ?? CHUNK_TYPE.CHART
    const mixable = MIXABLE_CHUNK_TYPES.includes(type)
    const units = Math.max(CHUNK_UNITS[type], Math.ceil(height / unitHeight))

    let startedNewPage = false
    if (unitsUsed > 0) {
      const typeConflict = !mixable && pageType !== null && pageType !== type
      const noRoom = unitsUsed + units > UNITS_PER_PAGE
      // 칸 수는 올림이라 실제 높이로는 넘칠 수 있다 — 높이도 함께 본다.
      const noHeight = height <= bottom - top && y + height > bottom

      if (typeConflict || noRoom || noHeight) {
        page += 1
        y = top
        unitsUsed = 0
        pageType = null
        prevWasHeader = false
        startedNewPage = true
      }
    }

    // 상단 요약 바로 아래는 조금 더 띄운다
    if (prevWasHeader && !mixable) {
      y += HEADER_BOTTOM_GAP_MM - gap
    }
    prevWasHeader = mixable

    if (!mixable) {
      pageType = type
    }

    // 한 페이지보다 큰 덩어리 — 잘라 이어 붙인다
    if (units > UNITS_PER_PAGE) {
      const slices: PageSlice[] = []
      let remaining = height
      let sourceOffset = 0
      let sliceY = y

      while (remaining > 0) {
        const sliceHeight = Math.min(remaining, bottom - sliceY)
        slices.push({ page, y: sliceY, sourceOffset, height: sliceHeight })

        remaining -= sliceHeight
        sourceOffset += sliceHeight

        if (remaining > 0) {
          page += 1
          sliceY = top
        } else {
          sliceY += sliceHeight
        }
      }

      y = sliceY + gap
      unitsUsed = Math.min(UNITS_PER_PAGE, Math.ceil((sliceY - top) / unitHeight))

      return {
        index,
        placed: true,
        page: slices[0].page,
        y: slices[0].y,
        height,
        slices,
        startedNewPage,
      }
    }

    const placedY = y
    y += height + gap
    unitsUsed += units

    return { index, placed: true, page, y: placedY, height, slices: [], startedNewPage }
  })

  return { placements, totalPages: page }
}
